/**
 * View renderer - state subscription & DOM update orchestrator
 */
import type { GameCore } from '@app-games/core';
import type { GameState, GameAction } from '../game/index.js';
import { getCurrentCPS, getCurrentClickPower } from '../game/state.js';
import { formatNumber } from '../game/utils.js';
import { t } from '../i18n/index.js';
import { TAB_ICONS } from './tabIcons.js';

export type Tab = 'main' | 'exchange' | 'coindetail' | 'leadroom' | 'leaderboard' | 'upgrades' | 'prestige' | 'darkpool' | 'achievements' | 'settings';

export interface ViewState {
  currentTab: Tab;
  selectedCoinId: string | null;
}

let viewState: ViewState = { currentTab: 'main', selectedCoinId: null };
let core: GameCore<GameState, GameAction>;

export function getViewState(): ViewState { return viewState; }
export function getCore(): GameCore<GameState, GameAction> { return core; }

let screenDirty = true;

const TAB_SCREEN_MAP: Record<Tab, string> = {
  main: 'main-screen',
  exchange: 'exchange-screen',
  coindetail: 'coindetail-screen',
  leadroom: 'leadroom-screen',
  leaderboard: 'leaderboard-screen',
  upgrades: 'upgrade-screen',
  prestige: 'prestige-screen',
  darkpool: 'darkpool-screen',
  achievements: 'ach-screen',
  settings: 'settings-screen',
};

function updateScreenVisibility() {
  const screenContent = document.getElementById('screen-content');
  if (!screenContent) return;
  for (const child of Array.from(screenContent.children) as HTMLElement[]) {
    const isActive = child.id === TAB_SCREEN_MAP[viewState.currentTab];
    child.classList.toggle('screen-active', isActive);
    child.classList.toggle('screen-hidden', !isActive);
  }
}

export function setViewState(partial: Partial<ViewState>) {
  viewState = { ...viewState, ...partial };
  screenDirty = true;
  updateScreenVisibility();
  updatePixiVisibility();
  requestRender();
}

function updatePixiVisibility() {
  const isMain = viewState.currentTab === 'main';
  const pixiSpacer = document.getElementById('pixi-spacer');
  const pixiContainer = document.getElementById('pixi-container');
  const coinOverlay = document.getElementById('coin-touch-overlay');
  const screenContent = document.getElementById('screen-content');
  if (pixiSpacer) pixiSpacer.style.display = isMain ? '' : 'none';
  if (pixiContainer) pixiContainer.style.display = isMain ? '' : 'none';
  if (coinOverlay) coinOverlay.style.display = isMain ? '' : 'none';
  if (screenContent) screenContent.classList.toggle('screen-fullheight', !isMain);
}

export function initRenderer(gameCore: GameCore<GameState, GameAction>) {
  core = gameCore;
}

interface RenderCallback {
  fn: () => void;
  alwaysTabs?: Tab[];
}

let renderEntries: RenderCallback[] = [];

export function onRender(cb: () => void, opts?: { alwaysTabs?: Tab[] }) {
  renderEntries.push({ fn: cb, alwaysTabs: opts?.alwaysTabs });
}

let renderScheduled = false;
export function requestRender() {
  if (renderScheduled) return;
  renderScheduled = true;
  requestAnimationFrame(() => {
    renderScheduled = false;
    renderAll();
  });
}

export function markScreenDirty() { screenDirty = true; }

function renderAll() {
  updateHUD();
  const tab = viewState.currentTab;
  if (screenDirty) {
    screenDirty = false;
    for (const entry of renderEntries) entry.fn();
  } else {
    for (const entry of renderEntries) {
      if (entry.alwaysTabs?.includes(tab)) entry.fn();
    }
  }
}

function updateHUD() {
  const st = core.getState();
  const cps = getCurrentCPS(st);
  const tp = getCurrentClickPower(st);

  const coinsEl = document.getElementById('hud-coins');
  const cpsEl = document.getElementById('hud-cps');
  const tapEl = document.getElementById('hud-tap');
  const comboEl = document.getElementById('hud-combo');

  const marketEl = document.getElementById('hud-market');

  if (coinsEl) coinsEl.textContent = formatNumber(st.coins);
  if (cpsEl) cpsEl.textContent = `${formatNumber(cps)}${t('hud_cps')}`;
  if (tapEl) tapEl.textContent = `${formatNumber(tp)}${t('hud_tap')}`;

  // Market multiplier badge (SVG trend arrow, no emoji)
  if (marketEl && st.market) {
    const mult = st.market.earningsMultiplier;
    const pct = Math.round(mult * 100);
    const up = mult >= 1.0;
    const trend = up
      ? '<svg class="hud-trend" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17l6-6 4 4 8-8"/><path d="M21 7v5h-5"/></svg>'
      : '<svg class="hud-trend" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7l6 6 4-4 8 8"/><path d="M21 17v-5h-5"/></svg>';
    marketEl.innerHTML = `${trend}${pct}%`;
    marketEl.className = up ? 'hud-market hud-market-up' : 'hud-market hud-market-down';
    marketEl.style.display = 'flex';
  }

  if (comboEl) {
    if (st.combo.count > 1) {
      comboEl.style.display = 'flex';
      comboEl.textContent = `${t('hud_combo')}${st.combo.count}`;
    } else {
      comboEl.style.display = 'none';
    }
  }

  updateEffectBadges(st);
}

function updateEffectBadges(st: GameState) {
  const bar = document.getElementById('fx-badges');
  if (!bar) return;
  const now = Date.now();
  const active = st.activeGoldenEffects.filter(e => now < e.expiresAt);
  const hasBooster = st.clickBooster.active && now < st.clickBooster.expiresAt;

  if (active.length === 0 && !hasBooster) {
    if (bar.innerHTML !== '') bar.innerHTML = '';
    return;
  }

  let html = '';
  for (const fx of active) {
    const rem = Math.ceil((fx.expiresAt - now) / 1000);
    if (rem <= 0) continue;
    const name = t('golden_' + fx.type) || fx.type;
    html += `<span class="effect-pill effect-golden">${name} ${rem}s</span>`;
  }
  if (hasBooster) {
    const rem = Math.ceil((st.clickBooster.expiresAt - now) / 1000);
    if (rem > 0) html += `<span class="effect-pill effect-turbo">${t('action_turbo')} ${rem}s</span>`;
  }
  bar.innerHTML = html;
}

export function showToast(message: string) {
  const el = document.getElementById('toast');
  if (!el) return;
  el.textContent = message;
  el.classList.add('show');
  setTimeout(() => el.classList.remove('show'), 2500);
}

export function showFloatText(x: number, y: number, text: string) {
  const el = document.createElement('div');
  el.className = 'float-text';
  el.textContent = text;
  el.style.left = `${x}px`;
  el.style.top = `${y}px`;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 800);
}

export function showModal(title: string, bodyHTML: string, buttons: { label: string; color: string; onClick: () => void }[]) {
  document.querySelector('.modal-overlay')?.remove();
  const ov = document.createElement('div');
  ov.className = 'modal-overlay';
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.innerHTML = `<div class="modal-title">${title}</div><div class="modal-body">${bodyHTML}</div>`;
  const btnRow = document.createElement('div');
  btnRow.className = 'modal-btns';
  for (const b of buttons) {
    const btn = document.createElement('button');
    btn.className = 'modal-btn';
    btn.style.background = b.color;
    btn.textContent = b.label;
    btn.addEventListener('click', () => { ov.remove(); b.onClick(); });
    btnRow.appendChild(btn);
  }
  modal.appendChild(btnRow);
  ov.appendChild(modal);
  ov.addEventListener('click', (e) => { if (e.target === ov) ov.remove(); });
  document.body.appendChild(ov);
}

export function dispatch(action: GameAction) {
  core.dispatch(action);
  if (action.type !== 'TICK') screenDirty = true;
  requestRender();
}

export function getState(): GameState {
  return core.getState();
}

const htmlCache = new WeakMap<HTMLElement, string>();
export function setInnerHTML(el: HTMLElement, html: string): boolean {
  if (htmlCache.get(el) === html) return false;
  htmlCache.set(el, html);
  el.innerHTML = html;
  return true;
}

export function buildLayout() {
  const root = document.createElement('div');
  root.id = 'game-ui';
  root.innerHTML = `
    <div id="hud">
      <div class="hud-left">
        <div id="hud-coins" class="hud-coins">0</div>
        <div class="hud-sub">
          <span id="hud-cps" class="hud-stat">0/s</span>
          <span id="hud-tap" class="hud-stat">0/tap</span>
        </div>
      </div>
      <div class="hud-right-col">
        <div id="hud-market" class="hud-market"></div>
        <div id="hud-combo" class="hud-combo"></div>
      </div>
    </div>
    <div id="fx-badges"></div>
    <div id="pixi-spacer"></div>
    <div id="screen-content"></div>
    <div id="tab-bar"></div>
    <div id="toast"></div>
  `;
  document.body.appendChild(root);

  // Build tab bar
  const tabBar = document.getElementById('tab-bar')!;
  const tabs: { id: Tab; icon: string; labelKey: string }[] = [
    { id: 'main', icon: TAB_ICONS.main, labelKey: 'tab_mine' },
    { id: 'exchange', icon: TAB_ICONS.exchange, labelKey: 'tab_exchange' },
    { id: 'upgrades', icon: TAB_ICONS.upgrades, labelKey: 'tab_upgrade' },
    { id: 'prestige', icon: TAB_ICONS.prestige, labelKey: 'tab_prestige' },
    { id: 'darkpool', icon: TAB_ICONS.darkpool, labelKey: 'tab_darkpool' },
    { id: 'achievements', icon: TAB_ICONS.achievements, labelKey: 'tab_achievement' },
    { id: 'settings', icon: TAB_ICONS.settings, labelKey: 'tab_settings' },
  ];
  for (const tab of tabs) {
    const el = document.createElement('div');
    el.className = 'tab-item';
    el.dataset.tab = tab.id;
    el.innerHTML = `<span class="tab-icon">${tab.icon}</span><span class="tab-label">${t(tab.labelKey)}</span>`;
    el.addEventListener('click', () => {
      setViewState({ currentTab: tab.id });
      updateTabBar();
    });
    tabBar.appendChild(el);
  }
  updateTabBar();
  updateScreenVisibility();
}

function updateTabBar() {
  const tabBar = document.getElementById('tab-bar');
  if (!tabBar) return;
  for (const el of Array.from(tabBar.children) as HTMLElement[]) {
    // coindetail + leadroom + leaderboard are sub-screens of exchange; keep exchange highlighted
    const effectiveTab: Tab = (viewState.currentTab === 'coindetail' || viewState.currentTab === 'leadroom' || viewState.currentTab === 'leaderboard')
      ? 'exchange'
      : viewState.currentTab;
    const active = el.dataset.tab === effectiveTab;
    el.classList.toggle('active', active);
  }
}

export { getCurrentCPS, getCurrentClickPower };
